/**
 * @file Live tuning layer over the camera rig and lens.
 *
 * `CONFIG.camera` is the shipped default. This module mirrors the player,
 * wave and weapon tuning layers: sparse numeric overrides, persisted only for
 * debug tooling, then merged back into a read-only camera settings view.
 */

import { CONFIG } from './GameConfig.js';
import { humanise } from '../utils/Text.js';

export const CAMERA_TUNING_STORAGE_KEY = 'spin.cameraTuning.v1';

/**
 * @type {Readonly<Record<string, { label: string, step: number, min?: number, max?: number }>>}
 */
const CAMERA_META = Object.freeze({
  fov: { label: 'FOV', step: 1, min: 20, max: 120 },
  near: { label: 'Near plane', step: 0.1, min: 0.01 },
  far: { label: 'Far plane', step: 5, min: 10 },
  tilt: { label: 'Tilt (rad)', step: 0.01, min: 0.05, max: 1.45 },
  distance: { label: 'Distance', step: 0.5, min: 1 },
  minDistance: { label: 'Min distance', step: 0.5, min: 1 },
  maxDistance: { label: 'Max distance', step: 0.5, min: 1 },
  zoomStep: { label: 'Zoom step', step: 0.1, min: 0.1 },
  damping: { label: 'Damping', step: 0.1, min: 0 },
  aimLead: { label: 'Aim lead', step: 0.01, min: 0 },
  maxAimLead: { label: 'Max aim lead', step: 0.5, min: 0 },
  'offset.x': { label: 'Offset X', step: 0.25, min: -100, max: 100 },
  'offset.y': { label: 'Offset Y', step: 0.25, min: -100, max: 100 },
  'offset.z': { label: 'Offset Z', step: 0.25, min: -100, max: 100 },
  fisheye: { label: 'Fisheye', step: 0.01, min: -1, max: 1 },
  'shake.decay': { label: 'Shake decay', step: 0.1, min: 0 },
  'shake.maxOffset': { label: 'Shake offset', step: 0.1, min: 0 },
  'shake.maxRoll': { label: 'Shake roll', step: 0.001, min: 0 },
  'shake.frequency': { label: 'Shake frequency', step: 0.5, min: 0 }
});

const DEFAULT_META = Object.freeze({ label: '', step: 0.1 });

/** @type {Record<string, number>} */
let overrides = {};
/** @type {object | null} */
let merged = null;
/** @type {Set<(tuning: typeof CONFIG.camera) => void>} */
const listeners = new Set();
/** @type {Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null} */
let storage = detectStorage();

function detectStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function readPath(root, path) {
  return path.split('.').reduce((node, seg) => (node == null ? undefined : node[seg]), root);
}

function buildMerged(prefix, node) {
  /** @type {Record<string, any>} */
  const result = {};
  for (const [key, value] of Object.entries(node)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = buildMerged(path, value);
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      result[key] = overrides[path] ?? value;
    } else {
      result[key] = value;
    }
  }
  return Object.freeze(result);
}

function invalidate() {
  merged = null;
  const tuning = getCameraTuning();
  for (const listener of listeners) listener(tuning);
}

export function setCameraTuningStorage(next) {
  storage = next;
}

export function getCameraTuning() {
  if (!merged) merged = buildMerged('', CONFIG.camera);
  return /** @type {typeof CONFIG.camera} */ (merged);
}

export function cameraTuningPaths() {
  /** @type {string[]} */
  const paths = [];
  const walk = (prefix, node) => {
    for (const [key, value] of Object.entries(node)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) walk(path, value);
      else if (typeof value === 'number' && Number.isFinite(value)) paths.push(path);
    }
  };
  walk('', CONFIG.camera);
  return paths;
}

export function getCameraFieldMeta(path) {
  return CAMERA_META[path] ?? { ...DEFAULT_META, label: humanise(path.split('.').pop() ?? path) };
}

export function getCameraDefaultValue(path) {
  const value = readPath(CONFIG.camera, path);
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function setCameraValue(path, rawValue) {
  const fallback = getCameraDefaultValue(path);
  if (fallback === undefined) return false;

  const parsed = typeof rawValue === 'number' ? rawValue : Number.parseFloat(String(rawValue));
  if (!Number.isFinite(parsed)) return false;

  const meta = getCameraFieldMeta(path);
  let value = parsed;
  if (meta.min !== undefined) value = Math.max(meta.min, value);
  if (meta.max !== undefined) value = Math.min(meta.max, value);

  if (value === fallback) delete overrides[path];
  else overrides[path] = value;

  invalidate();
  return true;
}

export function getCameraOverrides() {
  return { ...overrides };
}

export function isCameraValueOverridden(path) {
  return path in overrides;
}

export function applyCameraOverrides(next) {
  overrides = {};
  let applied = 0;

  for (const [path, value] of Object.entries(next ?? {})) {
    const fallback = getCameraDefaultValue(path);
    if (fallback === undefined || typeof value !== 'number' || !Number.isFinite(value)) continue;
    const meta = getCameraFieldMeta(path);
    let clamped = value;
    if (meta.min !== undefined) clamped = Math.max(meta.min, clamped);
    if (meta.max !== undefined) clamped = Math.min(meta.max, clamped);
    if (clamped === fallback) continue;
    overrides[path] = clamped;
    applied++;
  }

  invalidate();
  return applied;
}

export function saveCameraTuning() {
  if (!storage) return false;
  try {
    storage.setItem(CAMERA_TUNING_STORAGE_KEY, JSON.stringify(overrides));
    return true;
  } catch {
    return false;
  }
}

export function loadCameraTuning() {
  if (!storage) return 0;
  try {
    const raw = storage.getItem(CAMERA_TUNING_STORAGE_KEY);
    if (!raw) return 0;
    return applyCameraOverrides(JSON.parse(raw));
  } catch {
    return 0;
  }
}

export function resetCameraTuning() {
  overrides = {};
  try {
    storage?.removeItem(CAMERA_TUNING_STORAGE_KEY);
  } catch {
    // The in-memory reset already succeeded.
  }
  invalidate();
}

export function exportCameraTuning() {
  return JSON.stringify({ overrides: getCameraOverrides(), camera: getCameraTuning() }, null, 2);
}

export function onCameraTuningChanged(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function inspectCameraTuning() {
  const tuning = getCameraTuning();
  return {
    fields: cameraTuningPaths().map((path) => {
      const meta = getCameraFieldMeta(path);
      return {
        path,
        label: meta.label || humanise(path.split('.').pop() ?? path),
        value: /** @type {number} */ (readPath(tuning, path)),
        defaultValue: /** @type {number} */ (getCameraDefaultValue(path)),
        step: meta.step,
        min: meta.min,
        max: meta.max,
        overridden: isCameraValueOverridden(path)
      };
    })
  };
}
